import { Checkbox, FormControl, FormControlLabel, FormLabel, IconButton, InputAdornment, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField } from "@mui/material";
import React, { FC, forwardRef, ReactNode, Ref, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useDoorConfigurationBuilderActions } from "../../../../../../../store/reducers/door_configuration_builder";
import { useStoreDispatch, useStoreSelector } from "../../../../../../../hooks/StoreHooks";
import { Accessorio, Color, ColorSide, ColorType, IntegratedDoorConfiguration, IntegratedDoorMeasureType, IntegratedDoorOptions, Option } from "../../../../../../models/door_configuration";
import { useDialogs } from "../../../../../../providers/dialogs_provider";
import { useFormik } from "formik";
import { DoorType, FinishType, IntegratedDoorPosition, NullableString, OpeningDirection, Optional } from "../../../../../../core/common_types";
import { useOverlapConfigurationsService } from "../../../../../../services/overlap_configurations_service";
import { getCurrentUser } from "../../../../../../core/common_functions";
import { useFinishes } from "../../../../../../providers/finish_provider";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import finishes from "../../../../../../assets/finishes.json";
import rals from "../../../../../../assets/rals.json";

// H esterno telaio porta basculante Overbas -> H esterno telaio portina pedonale integrata
const ALTEZZE_PORTINA_OVERBAS: {altezzaPorta: number, altezzaPortina: number}[] = [
    {altezzaPorta: 2380, altezzaPortina: 2100},
    {altezzaPorta: 2330, altezzaPortina: 2050},
    {altezzaPorta: 2280, altezzaPortina: 1950},
    {altezzaPorta: 2230, altezzaPortina: 1950},
    {altezzaPorta: 2180, altezzaPortina: 1900},
    {altezzaPorta: 2130, altezzaPortina: 1850},
    {altezzaPorta: 2080, altezzaPortina: 1800},
    {altezzaPorta: 2030, altezzaPortina: 1750},
    {altezzaPorta: 1980, altezzaPortina: 1700},
    {altezzaPorta: 1930, altezzaPortina: 1650}
];

const DELTA_TELAIO_PORTINA = 50;

const LARGHEZZA_PORTINA_OVERBAS = 850;

const getAltezzaPortinaOverbas = (altezzaPorta: number): number => {
    let riga = ALTEZZE_PORTINA_OVERBAS.find(r => altezzaPorta >= r.altezzaPorta);
    return (riga ?? ALTEZZE_PORTINA_OVERBAS[ALTEZZE_PORTINA_OVERBAS.length-1]).altezzaPortina;
};

type FormPortinaIntegrataProps = {
    options: IntegratedDoorOptions
}

type FormPortinaIntegrataRef = {
    getData: () => Promise<IntegratedDoorConfiguration>
}

export type FormPortinaIntegrataTypeRef = React.ElementRef<typeof FormPortinaIntegrata>;

export const FormPortinaIntegrata = forwardRef((props: FormPortinaIntegrataProps, ref: Ref<FormPortinaIntegrataRef>) => {
    const { doorConfigurationBuilder } = useStoreSelector(store => store);
    const dispatcher = useStoreDispatch();
    const {setPortinaIntegrata, resetPortinaIntegrata} = useDoorConfigurationBuilderActions();
    const finishesProvider = useFinishes();
    const dialogs = useDialogs();
    
    const formik = useFormik<IntegratedDoorConfiguration>({
        initialValues: {
            portina: true, // la portina integrata è obbligatoria in Overbas
            altezza: doorConfigurationBuilder.configuration?.portinaIntegrata?.altezza ?? null,
            larghezza: doorConfigurationBuilder.configuration?.portinaIntegrata?.larghezza ?? null,
            bbox: doorConfigurationBuilder.configuration?.portinaIntegrata?.bbox ?? null,
            externalFrameColorType: doorConfigurationBuilder.configuration?.portinaIntegrata?.externalFrameColorType ?? "ral_standard",
            externalFrameColorCode: doorConfigurationBuilder.configuration?.portinaIntegrata?.externalFrameColorCode ?? undefined,
            internalFrameColorType: doorConfigurationBuilder.configuration?.portinaIntegrata?.internalFrameColorType ?? "ral_standard",
            internalFrameColorCode: doorConfigurationBuilder.configuration?.portinaIntegrata?.internalFrameColorCode ?? undefined,
            externalHandleColorType: doorConfigurationBuilder.configuration?.portinaIntegrata?.externalHandleColorType ?? "ral_standard",
            externalHandleColorCode: doorConfigurationBuilder.configuration?.portinaIntegrata?.externalHandleColorCode ?? undefined,
            internalHandleColorType: doorConfigurationBuilder.configuration?.portinaIntegrata?.internalHandleColorType ?? "ral_standard",
            internalHandleColorCode: doorConfigurationBuilder.configuration?.portinaIntegrata?.internalHandleColorCode ?? undefined,
            apertura: doorConfigurationBuilder.configuration?.portinaIntegrata?.apertura ?? "sx",
            posizione: doorConfigurationBuilder.configuration?.portinaIntegrata?.posizione ?? "c",
            fuoriMisura: doorConfigurationBuilder.configuration?.portinaIntegrata?.fuoriMisura ?? "standard",
            accessori: doorConfigurationBuilder.configuration?.portinaIntegrata?.accessori ?? [],
            soglia: doorConfigurationBuilder.configuration?.portinaIntegrata?.soglia ?? {rialzata: false, altezza: 0}
        },
        onSubmit: (model) => {

        }
    });

    const renderFormPortina = () => {
        return (
            <>
                <TextField
                    variant="outlined"
                    label="H esterno telaio portina"
                    className="w-full mt-3"
                    value={formik.values.altezza?.esternoTelaio ?? ""}
                    InputProps={{
                        readOnly: true,
                        endAdornment: <InputAdornment position="start">mm</InputAdornment>,
                    }}
                    helperText="Misura calcolata in base all'altezza esterno telaio della porta"
                />

                <FormControl fullWidth className="mt-3">
                    <InputLabel id="posizione">Posizione</InputLabel>
                    <Select
                        labelId="posizione"
                        value={formik.values.posizione}
                        label="Posizione"
                        onChange={(e) => {
                            const posizione = e.target.value as IntegratedDoorPosition;

                            let values: IntegratedDoorConfiguration = {
                                ...formik.values,
                                posizione
                            };

                            formik.setValues(values);
                            dispatcher(setPortinaIntegrata(values));
                        }}>
                        <MenuItem value={"sx"}>Decentrata a sinistra</MenuItem>
                        <MenuItem value={"c"}>Centrata</MenuItem>
                        <MenuItem value={"dx"}>Decentrata a destra</MenuItem>
                    </Select>
                </FormControl>

                <FormControl fullWidth className="mt-3 mb-3">
                    <InputLabel id="apertura">Verso di apertura (vista esterna)</InputLabel>
                    <Select
                        labelId="apertura"
                        value={formik.values.apertura}
                        label="Verso di apertura (vista esterna)"
                        onChange={(e) => {
                            const apertura = e.target.value as OpeningDirection;

                            let values: IntegratedDoorConfiguration = {
                                ...formik.values,
                                apertura
                            };
                            
                            formik.setValues(values);
                            dispatcher(setPortinaIntegrata(values));                            
                        }}>
                        <MenuItem value={"sx"}>Tirare a sinistra</MenuItem>
                        <MenuItem value={"dx"}>Tirare a destra</MenuItem>
                    </Select>
                </FormControl>

                {!doorConfigurationBuilder.isLaminato() && !doorConfigurationBuilder.isPietravera() && !doorConfigurationBuilder.isColoreCampione() && renderLockedExternalFrameColor()}

                {!doorConfigurationBuilder.isLaminato() && !doorConfigurationBuilder.isPietravera() && renderSelectInternalFrameColorType()}
                {formik.values.internalFrameColorType == "ral_non_standard" && renderInputInternalFrameNsRal()}

                {renderSelectExternalHandleColorType()}
                {formik.values.externalHandleColorType == "ral_non_standard" && renderInputExternalHandleNsRal()}

                {renderSelectInternalHandleColorType()}
                {formik.values.internalHandleColorType == "ral_non_standard" && renderInputInternalHandleNsRal()}

                <FormControl fullWidth className="mb-3">
                    <InputLabel id="tipologia_soglia">Tipologia soglia</InputLabel>
                    <Select
                        labelId="tipologia_soglia"
                        value={formik.values.soglia?.rialzata ?? false}
                        label="Tipologia soglia"
                        onChange={(e) => {
                            let values: IntegratedDoorConfiguration = {
                                ...formik.values,
                                soglia: {
                                    rialzata: e.target.value == "true",
                                    altezza: 0
                                }
                            };
                            formik.setValues(values);
                            dispatcher(setPortinaIntegrata(values));
                        }}>
                        <MenuItem value={"false"}>Ribassata</MenuItem>
                        <MenuItem value={"true"}>Rialzata</MenuItem>
                    </Select>
                </FormControl>
                {(formik.values.soglia?.rialzata ?? false) && <TextField
                    error={formik.errors.soglia?.altezza != null}
                    variant="outlined"
                    label="Altezza soglia"
                    type={"number"}
                    className="w-full mb-3"
                    value={formik.values.soglia?.altezza ?? 0}
                    InputProps={{
                        inputProps: {min: 0, max: 300},
                        endAdornment: <InputAdornment position="start">mm</InputAdornment>,
                    }}
                    onChange={e => {
                        let values: IntegratedDoorConfiguration = {
                            ...formik.values,
                            soglia: {
                                rialzata: true,
                                altezza: Number(e.target.value)
                            }
                        };
                        formik.setValues(values);
                        dispatcher(setPortinaIntegrata(values));
                    }}
                    onBlur={e => {
                        let h = Number(e.target.value);
                        if (h < 0) h = 0;
                        if (h > 300) h = 300;

                        let values: IntegratedDoorConfiguration = {
                            ...formik.values,
                            soglia: {
                                rialzata: h > 0,
                                altezza: h
                            }
                        };
                        formik.setValues(values);
                        dispatcher(setPortinaIntegrata(values));
                    }}
                    helperText={formik.errors.altezza ?? null}
                />}

                <h6 className="text-primary mt-2">Optional</h6>                
                {props.options.accessoriVari.map(a => <FormControlLabel
                    key={a?.id}
                    className="w-full mt-2"
                    control={
                        <Checkbox
                            checked={formik.values.accessori.filter(fm => fm.sku == a?.sku).length > 0}
                            onChange={(e) => {
                                let accessori = formik.values.accessori.map(a => ({...a}));
                                if (e.target.checked) {
                                    if (a && !accessori.find(fm => fm.sku == a?.sku)) {
                                        accessori.push({sku: a.sku, notes: ""});
                                    }
                                } else {
                                    accessori = accessori.filter(fm => fm.sku != a?.sku);
                                }
                                let values: IntegratedDoorConfiguration = {
                                    ...formik.values,
                                    accessori
                                };
                                formik.setValues(values);
                                dispatcher(setPortinaIntegrata(values));
                            }}
                            sx={{ marginTop: "0", marginBottom: "0" }}
                        />
                    }
                    label={
                        <span>{a?.name}</span>
                    }
                    sx={{ marginTop: "0", marginBottom: "0" }}
                />)}
            </>
        );
    }

    // il colore del telaio esterno della portina e' bloccato su quello della porta
    const renderLockedExternalFrameColor = () => {
        const code = doorConfigurationBuilder.getExternalFrameColorCode();

        return (
            <TextField
                fullWidth
                focused={false}
                className="mb-3"
                label={"Colore telaio esterno"}
                value={getRalName(code)}
                InputProps={{
                    readOnly: true,
                    startAdornment: code && (
                        <InputAdornment position="start">
                            {renderFinishPreview(code)}
                        </InputAdornment>
                    ),
                }}
                helperText="Colore uguale a quello del telaio esterno della porta"
            />
        );
    }

    const renderSelectExternalFrameColorType = () => {
        if (!doorConfigurationBuilder.configuration) return null;

        return (
            <FormControl fullWidth className="mb-3">
                <InputLabel id="colore_telaio_esterno">Colore telaio esterno</InputLabel>
                <Select
                    labelId="colore_telaio_esterno"
                    value={getColorTypeValue("external")}
                    label={"Colore telaio esterno"}
                    onChange={(e) => {
                        let value = e.target.value;
                        let externalFrameColorType: ColorType = "ral_standard";
                        let externalFrameColorCode: string|undefined = undefined;
                        if (value == "ral_standard" || value == "ral_non_standard") {
                            externalFrameColorType = value as ColorType;
                            externalFrameColorCode = undefined;
                        } else {
                            externalFrameColorCode = value;
                        }

                        let values: IntegratedDoorConfiguration = {
                            ...formik.values,
                            externalFrameColorType,
                            externalFrameColorCode,
                            internalFrameColorType: externalFrameColorType,
                            internalFrameColorCode: externalFrameColorCode
                        };
                        dispatcher(setPortinaIntegrata(values));
                        formik.setValues(values);
                    }}>
                    {renderStandardColorTypesMenuItems("external")}                    
                    <MenuItem value={"ral_non_standard"}>Colore personalizzato</MenuItem>
                </Select>
            </FormControl>
        );
    }

    const renderInputExternalFrameNsRal = () => {
        return (
            <TextField
                fullWidth 
                focused={false}
                className="mb-3"
                label={"Colore telaio esterno"}
                name={"coloreTelaioEsterno"}
                value={finishesProvider?.getRalName(formik.values.externalFrameColorCode)}
                InputProps={{
                    readOnly: true, 
                    startAdornment: formik.values.externalFrameColorCode && (
                        <InputAdornment position="start">
                            {finishesProvider?.getRalPreview(formik.values.externalFrameColorCode)}
                        </InputAdornment>
                    ),

                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => dialogs?.selectRalColor({
                                skipRals: getSkippedRalStandard("external"),
                                onConfirm: selectedColor => {                                
                                let values: IntegratedDoorConfiguration = {
                                    ...formik.values,
                                    externalFrameColorCode: selectedColor,
                                    internalFrameColorCode: selectedColor,
                                };
                                dispatcher(setPortinaIntegrata(values));
                                formik.setValues(values);
                            }})}>
                                <OpenInNewIcon style={{ color: "black" }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        );
    }

    const renderSelectInternalFrameColorType = () => {
        if (!doorConfigurationBuilder.configuration) return null;

        return (
            <FormControl fullWidth className="mb-3">
                <InputLabel id="colore_telaio_interno">Colore telaio interno</InputLabel>
                <Select
                    labelId="colore_telaio_interno"
                    value={getColorTypeValue("internal")}
                    label={"Colore telaio interno"}
                    disabled={formik.values.externalFrameColorCode == doorConfigurationBuilder.getExternalFrameColorCode() && doorConfigurationBuilder.isWoodstyle()}
                    onChange={(e) => {
                        let value = e.target.value;
                        let internalFrameColorType: ColorType = "ral_standard";
                        let internalFrameColorCode: string|undefined = undefined;
                        if (value == "ral_standard" || value == "ral_non_standard") {
                            internalFrameColorType = value as ColorType;
                            internalFrameColorCode = undefined;
                        } else {
                            internalFrameColorCode = value;
                        }

                        let values: IntegratedDoorConfiguration = {
                            ...formik.values,
                            internalFrameColorType,
                            internalFrameColorCode
                        };
                        dispatcher(setPortinaIntegrata(values));
                        formik.setValues(values);
                    }}>
                    {renderStandardColorTypesMenuItems("internal")}                    
                    <MenuItem value={"ral_non_standard"}>Colore personalizzato</MenuItem>
                </Select>
            </FormControl>
        );
    }

    const renderInputInternalFrameNsRal = () => {
        return (
            <TextField
                fullWidth 
                focused={false}
                className="mb-3"
                label={"Colore telaio interno"}
                name={"coloreTelaioInternoNsRal"}
                value={finishesProvider?.getRalName(formik.values.internalFrameColorCode)}
                InputProps={{
                    readOnly: true, 
                    startAdornment: formik.values.internalFrameColorCode && (
                        <InputAdornment position="start">
                            {finishesProvider?.getRalPreview(formik.values.internalFrameColorCode)}
                        </InputAdornment>
                    ),

                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => dialogs?.selectRalColor({
                                skipRals: getSkippedRalStandard("internal"),
                                onConfirm: selectedColor => {                                
                                let values: IntegratedDoorConfiguration = {
                                    ...formik.values,
                                    internalFrameColorCode: selectedColor
                                };
                                dispatcher(setPortinaIntegrata(values));
                                formik.setValues(values);
                            }})}>
                                <OpenInNewIcon style={{ color: "black" }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        );
    }

    const getColorTypeValue = (colorSide: ColorSide): string => {
        if (colorSide == "external") {
            if (formik.values.externalFrameColorType == "ral_standard") {
                return formik.values.externalFrameColorCode ?? "";
            }
            return formik.values.externalFrameColorType;
        } else {
            if (formik.values.internalFrameColorType == "ral_standard") {
                return formik.values.internalFrameColorCode ?? "";
            }
            return formik.values.internalFrameColorType;
        }
    }

    const renderStandardColorTypesMenuItems = (colorSide: ColorSide) => {
        let colors: string[] = [];

        let externalColorCode = doorConfigurationBuilder.getExternalFrameColorCode();
        if (externalColorCode && !colors.includes(externalColorCode)) colors.push(externalColorCode);

        if (colorSide == "internal") {
            let internalColorCode = doorConfigurationBuilder.getInternalFrameColorCode();
            if (internalColorCode && !colors.includes(internalColorCode)) colors.push(internalColorCode);
        }
        
        return colors.map(c => (
            <MenuItem value={c}>
                <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%"}}>
                    {getRalName(c)}
                    {renderFinishPreview(c)}
                </div>
            </MenuItem>
        ));
    }
    const getSkippedRalStandard = (colorSide: ColorSide): string[] => {
        let colors: string[] = [];

        let externalColorCode = doorConfigurationBuilder.getExternalFrameColorCode();
        if (externalColorCode && !colors.includes(externalColorCode)) colors.push(externalColorCode);

        if (colorSide == "internal") {
            let internalColorCode = doorConfigurationBuilder.getInternalFrameColorCode();
            if (internalColorCode && !colors.includes(internalColorCode)) colors.push(internalColorCode);
        }
        return colors;

    }
    const renderSelectExternalHandleColorType = () => {
        if (!doorConfigurationBuilder.configuration) return null;

        return (
            <FormControl fullWidth className="mb-3">
                <InputLabel id="tipo_colore_maniglia_esterna">Colore maniglia esterna</InputLabel>
                <Select
                    labelId="tipo_colore_maniglia_esterna"
                    value={formik.values.externalHandleColorType ?? "ral_standard"}
                    label={"Colore maniglia esterna"}
                    onChange={(e) => {
                        let values: IntegratedDoorConfiguration = {
                            ...formik.values,
                            externalHandleColorType: e.target.value as ColorType,
                            externalHandleColorCode: undefined
                        };
                        dispatcher(setPortinaIntegrata(values));
                        formik.setValues(values);
                    }}>
                    <MenuItem value={"ral_standard"}>Nero</MenuItem>
                    <MenuItem value={"ral_non_standard"}>Colore personalizzato</MenuItem>
                </Select>
            </FormControl>
        );
    }

    const renderInputExternalHandleNsRal = () => {
        return (
            <TextField
                fullWidth 
                focused={false}
                className="mb-3"
                label={"Specificare colore maniglia esterna"}
                name={"coloreManigliaEsterna"}
                value={finishesProvider?.getRalName(formik.values.externalHandleColorCode)}
                InputProps={{
                    readOnly: true, 
                    startAdornment: formik.values.externalHandleColorCode && (
                        <InputAdornment position="start">
                            {finishesProvider?.getRalPreview(formik.values.externalHandleColorCode)}
                        </InputAdornment>
                    ),

                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => dialogs?.selectRalColor({onConfirm: selectedColor => {                                
                                let values: IntegratedDoorConfiguration = {
                                    ...formik.values,
                                    externalHandleColorCode: selectedColor
                                };
                                dispatcher(setPortinaIntegrata(values));
                                formik.setValues(values);
                            }})}>
                                <OpenInNewIcon style={{ color: "black" }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        );
    }

    const renderSelectInternalHandleColorType = () => {
        if (!doorConfigurationBuilder.configuration) return null;

        return (
            <FormControl fullWidth className="mb-3">
                <InputLabel id="tipo_colore_maniglia_interna">Colore maniglia interna</InputLabel>
                <Select
                    labelId="tipo_colore_maniglia_interna"
                    value={formik.values.internalHandleColorType ?? "ral_standard"}
                    label={"Colore maniglia interna"}
                    onChange={(e) => {
                        let values: IntegratedDoorConfiguration = {
                            ...formik.values,
                            internalHandleColorType: e.target.value as ColorType,
                            internalHandleColorCode: undefined
                        };
                        dispatcher(setPortinaIntegrata(values));
                        formik.setValues(values);
                    }}>
                    <MenuItem value={"ral_standard"}>Nero</MenuItem>
                    <MenuItem value={"ral_non_standard"}>Colore personalizzato</MenuItem>
                </Select>
            </FormControl>
        );
    }

    const renderInputInternalHandleNsRal = () => {
        return (
            <TextField
                fullWidth 
                focused={false}
                className="mb-3"
                label={"Specificare colore maniglia interna"}
                name={"coloreManigliaInterna"}
                value={finishesProvider?.getRalName(formik.values.internalHandleColorCode)}
                InputProps={{
                    readOnly: true, 
                    startAdornment: formik.values.internalHandleColorCode && (
                        <InputAdornment position="start">
                            {finishesProvider?.getRalPreview(formik.values.internalHandleColorCode)}
                        </InputAdornment>
                    ),

                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => dialogs?.selectRalColor({onConfirm: selectedColor => {                                
                                let values: IntegratedDoorConfiguration = {
                                    ...formik.values,
                                    internalHandleColorCode: selectedColor
                                };
                                dispatcher(setPortinaIntegrata(values));
                                formik.setValues(values);
                            }})}>
                                <OpenInNewIcon style={{ color: "black" }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        );
    }

    const getRalName = (colorCode: NullableString) => {
        if (!colorCode) return "";

        let color = doorConfigurationBuilder.colors.find(c => c?.code == colorCode);        
        return color ? `${color.name} (${color.code})` : finishesProvider?.getRalName(colorCode);
    }

    const renderFinishPreview = (colorCode: NullableString) => {
        if (!colorCode) return null;
        
        let preview = finishes.find(f => f.code == colorCode)?.img;
        if (preview) return <img src={preview} style={{width: "2rem", height: "1.75rem"}} />;

        preview = rals.find(f => f.RAL == colorCode)?.HEX;
        if (preview) return <div style={{width: "1.75rem", height: "1.75rem", backgroundColor: preview}}></div>;

        return null;        
    }

    const enablePortinaIntegrata = (isEnabled: boolean) => {
        if (!doorConfigurationBuilder.configuration) return;
        
        if (!isEnabled) {
            let values: IntegratedDoorConfiguration = {
                portina: isEnabled, 
                altezza: null, 
                larghezza: null, 
                bbox: null,
                externalFrameColorType: "ral_standard",
                externalFrameColorCode: undefined,
                internalFrameColorType: "ral_standard",
                internalFrameColorCode: undefined,
                externalHandleColorType: "ral_standard",
                externalHandleColorCode: undefined,
                internalHandleColorType: "ral_standard",
                internalHandleColorCode: undefined,
                accessori: [], 
                apertura: "sx", 
                posizione: "c",
                fuoriMisura: "undefined",
                soglia: {rialzata: false, altezza: 0}
            };
            formik.setValues(values);
            dispatcher(resetPortinaIntegrata());
            return;
        }

        let frameColorType: ColorType = "ral_standard";
        let frameColorCode = doorConfigurationBuilder.getExternalFrameColorCode();
        if (doorConfigurationBuilder.isLaminato() || doorConfigurationBuilder.isPietravera() || doorConfigurationBuilder.isColoreCampione()) {
            frameColorType = "ral_non_standard";
            frameColorCode = undefined;
        }
        
        const altezzaPortina = getAltezzaPortinaOverbas(doorConfigurationBuilder.configuration.altezzaPorta);

        let values: IntegratedDoorConfiguration = {
            ...formik.values,
            portina: isEnabled,
            altezza: {esternoTelaio: altezzaPortina, internoTelaio: altezzaPortina - DELTA_TELAIO_PORTINA},
            larghezza: {esternoTelaio: LARGHEZZA_PORTINA_OVERBAS, internoTelaio: LARGHEZZA_PORTINA_OVERBAS},
            externalFrameColorType: frameColorType,
            externalFrameColorCode: frameColorCode,
            internalFrameColorType: "ral_standard",
            internalFrameColorCode: getDoorInternalFrameColor() ?? frameColorCode,
            fuoriMisura: "standard"
        };
        formik.setValues(values);
        dispatcher(setPortinaIntegrata(values));
    }

    // il colore telaio esterno della portina segue sempre quello della porta
    useEffect(() => {
        if (!formik.values.portina || !doorConfigurationBuilder.configuration) return;
        if (doorConfigurationBuilder.isLaminato() || doorConfigurationBuilder.isPietravera() || doorConfigurationBuilder.isColoreCampione()) return;

        const code = doorConfigurationBuilder.getExternalFrameColorCode() ?? undefined;
        if (formik.values.externalFrameColorType == "ral_standard" && formik.values.externalFrameColorCode == code) return;

        let values: IntegratedDoorConfiguration = {
            ...formik.values,
            externalFrameColorType: "ral_standard",
            externalFrameColorCode: code
        };
        formik.setValues(values);
        dispatcher(setPortinaIntegrata(values));
    }, [formik.values.portina, doorConfigurationBuilder.getExternalFrameColorCode()]);

    const getDoorInternalFrameColor = (): string|undefined => {
        if (doorConfigurationBuilder.isLaminato() || doorConfigurationBuilder.isPietravera()) return undefined;
        return doorConfigurationBuilder.getInternalFrameColorCode() ?? undefined;
    }

    // il colore telaio interno della portina parte da quello interno della porta (modificabile)
    const lastDoorInternalColor = useRef<string|undefined>(undefined);
    useEffect(() => {
        if (!formik.values.portina || !doorConfigurationBuilder.configuration) return;

        const code = getDoorInternalFrameColor();
        const current = formik.values.internalFrameColorCode ?? undefined;
        const previous = lastDoorInternalColor.current;
        lastDoorInternalColor.current = code;

        if (!code) return;
        // si aggiorna solo se vuoto o se seguiva ancora il colore precedente della porta
        if (current && current != previous && current != code) return;
        if (formik.values.internalFrameColorType == "ral_standard" && current == code) return;

        let values: IntegratedDoorConfiguration = {
            ...formik.values,
            internalFrameColorType: "ral_standard",
            internalFrameColorCode: code
        };
        formik.setValues(values);
        dispatcher(setPortinaIntegrata(values));
    }, [formik.values.portina, doorConfigurationBuilder.getInternalFrameColorCode()]);

    // la misura della portina segue sempre l'altezza esterno telaio della porta
    useEffect(() => {
        if (!formik.values.portina || !doorConfigurationBuilder.configuration) return;

        const altezzaPortina = getAltezzaPortinaOverbas(doorConfigurationBuilder.configuration.altezzaPorta);
        if (formik.values.altezza?.esternoTelaio == altezzaPortina &&
            formik.values.larghezza?.esternoTelaio == LARGHEZZA_PORTINA_OVERBAS) return;

        let values: IntegratedDoorConfiguration = {
            ...formik.values,
            altezza: {esternoTelaio: altezzaPortina, internoTelaio: altezzaPortina - DELTA_TELAIO_PORTINA},
            larghezza: {esternoTelaio: LARGHEZZA_PORTINA_OVERBAS, internoTelaio: LARGHEZZA_PORTINA_OVERBAS}
        };
        formik.setValues(values);
        dispatcher(setPortinaIntegrata(values));
    }, [formik.values.portina, doorConfigurationBuilder.configuration?.altezzaPorta]);

    useImperativeHandle(ref, () => ({
        async getData(): Promise<IntegratedDoorConfiguration> {
                        
            // i campi colore vanno sempre inviati (null se vuoti) per evitare "undefined property" nel backend
            return {
                ...formik.values,
                externalFrameColorCode: formik.values.externalFrameColorCode ?? null,
                internalFrameColorCode: formik.values.internalFrameColorCode ?? null,
                externalHandleColorCode: formik.values.externalHandleColorCode ?? null,
                internalHandleColorCode: formik.values.internalHandleColorCode ?? null,
            };
        }
    }));

    return (
        <div>
            {/* <FormControlLabel
                className="w-full mt-1"
                control={
                    <Checkbox
                        checked={formik.values.portina}
                        onChange={(e) => enablePortinaIntegrata(e.target.checked)}
                        sx={{ marginTop: "0", marginBottom: "0" }}
                    />
                }
                label={props.options.descrizione}
                sx={{ marginTop: "0", marginBottom: "0" }}
            /> */}

            <FormControl fullWidth className="mt-3">
                {/* <InputLabel id="fuori_misura">Dimensione</InputLabel> */}
                <Select
                    labelId="fuori_misura"
                    value="classic"
                    label=""
                    onChange={() => {}}>
                    <MenuItem value={"classic"}>Portina Classic</MenuItem>
                </Select>
            </FormControl>

            {formik.values.portina && renderFormPortina()}
        </div>
    );
});